/**
 * runOperationsTrailModel — one run's multi-stage story, read from projections the product already
 * ships
 * (FULL-0113 / PROD-OPSUX2-027).
 *
 * MASTER_FLOW §15A's required direction is a *"run timeline that shows tool calls, approvals,
 * outcomes, errors, latency/cost and verification evidence without exposing hidden model
 * chain-of-thought or secrets"*. `docs/EMPLOYEE_OPERATIONS_UX.md` §8 records that the per-employee
 * operations screen (FULL-0100) is NOT that timeline. This module is the reading half of the
 * timeline, and it introduces no producer: every fact it returns already exists on one of three
 * reads the run page made (or now makes) under the same `agents_view` grant as `GET /{run_id}`:
 *
 *   - `GET /api/agent-runs/{run_id}` — `analysis`, `actions`, telemetry fields;
 *   - `GET /api/agent-runs/{run_id}/approval-tokens` — the non-secret approval-token audit rows;
 *   - `GET /api/agent-runs/{run_id}/qa-inspection` — the verdict, findings, missing evidence and
 *     closure judgement of the QA / Decision Inspector (`app/services/run_qa_inspector.py`).
 *
 * It fetches nothing, decides nothing, writes nothing and grants nothing. In particular it does not
 * re-grade a QA verdict, does not infer that a missing field is zero, and does not print a raw
 * producer token where a shared vocabulary has a word.
 *
 * TRUTHFULNESS RULES (pinned by `runOperationsTrailModel.test.js`):
 *   - absent, empty and unreadable are three different sentences. A step the payload did not carry
 *     says "not reported"; a step the payload carried empty says zero; a step whose shape this build
 *     does not understand says so instead of drawing a clean result;
 *   - a value outside a producer scale is worded by the shared unknown arm, never echoed. The one
 *     deliberate exception is `actionTypeLabel`, whose documented contract is to echo an unmapped
 *     token rather than invent a Hebrew name for it;
 *   - telemetry distinguishes `0` from "not reported". `estimated_cost: 0` is a free run, not a
 *     missing figure — a truthiness gate would hide exactly the arm an operator reads as "no cost";
 *   - an approval token's state is derived only from its own flags and expiry. A row with neither a
 *     readable expiry nor boolean flags is UNKNOWN, not "active": a token nobody can date is not an
 *     unexpired token.
 *
 * NOT CHAIN-OF-THOUGHT. Nothing here reads or reconstructs hidden reasoning. `analysis` is the
 * product's own answer (summary, confidence, evidence sources, proposed action), and the QA
 * projection is the server's audit of decision inputs. Both were already rendered by the run page;
 * this module only puts them in stage order.
 */
import { parseUtc } from "../utils/utcDate";
import { isHumanDecisionStatus } from "./employeeOperations";

/** The stages, in the order an operator follows them. */
export const RUN_TRAIL_STAGES = [
  {
    key: "analysis",
    title: "ניתוח ובסיס הראיות",
    description: "מה העובד הסיק, באיזו מידה, ומאילו מקורות ראיה.",
  },
  {
    key: "proposal",
    title: "פעולה מוצעת",
    description: "מה העובד הציע לעשות, על מה, ומסוג איזה כלי.",
  },
  {
    key: "approval",
    title: "אישור",
    description: "החלטה אנושית פתוחה ואסימוני האישור שנרשמו לריצה. ההחלטה עצמה ניתנת בפקדים שלמעלה.",
  },
  {
    key: "execution",
    title: "ביצוע וקריאות כלים",
    description: "הפעולות שרשם העובד לפי סדר, כולל כשלים ומשך כל פעולה.",
  },
  {
    key: "verification",
    title: "אימות הראיות",
    description: "סיווג בקרת האיכות, כשירות הסגירה והראיות שחסרות — כפי שהשרת הכריע.",
  },
];

export const RUN_TRAIL_STAGE_KEYS = RUN_TRAIL_STAGES.map((stage) => stage.key);

/** The words for an approval token's derived state. `unknown` is a state, not a guess. */
export const APPROVAL_TOKEN_STATE_LABEL = {
  active: "פעיל",
  used: "הופעל",
  revoked: "בוטל",
  expired: "פג תוקף",
  unknown: "מצב אסימון לא ידוע",
};

/**
 * `initiated_by` is a closed producer vocabulary plus one free-form arm (an operator's email when a
 * human started the run). The known arms get words; the free-form arm is bounded to its local part,
 * exactly as the telemetry card this trail replaces did.
 */
export const INITIATED_BY_LABEL = {
  auto_trigger: "אוטומטי",
  "auto:new_ticket": "אוטומטי — קריאה חדשה",
  "auto:email_reply": "אוטומטי — תגובת לקוח",
  "auto:scheduled_check": "אוטומטי — בדיקה מתוזמנת",
  manual: "ידני",
};

export function initiatedByLabel(value) {
  const raw = text(value);
  if (!raw) return null;
  if (INITIATED_BY_LABEL[raw]) return INITIATED_BY_LABEL[raw];
  return raw.includes("@") ? raw.split("@")[0] : raw;
}

function text(value) {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}

function finiteNonNegative(value) {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : null;
}

function plainObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : null;
}

/**
 * True when the approve/reject routes can act on a token. Kept here (and re-exported by the run
 * page) because the trail is the only renderer of a token row now.
 *
 * A naive `expires_at` is read as UTC via `parseUtc`: `ApprovalToken.expires_at` is serialized
 * without a `Z`, and `new Date(naive)` would read it as local browser time.
 */
export function isApprovalTokenExpired(token, now = Date.now()) {
  if (!token || token.used || token.revoked || !token.expires_at) return false;
  const at = parseUtc(token.expires_at).getTime();
  return !Number.isNaN(at) && at <= now;
}

/**
 * The first stage: what the employee concluded and on what evidence.
 *
 * `arm` is one of `absent` (no analysis recorded), `empty` (an empty object was recorded),
 * `unreadable` (a non-object arrived) or `present`.
 */
export function readAnalysisStage(analysis) {
  if (analysis == null) {
    return { key: "analysis", arm: "absent", summary: null, confidence: null, riskLevel: null, degraded: null, evidenceSources: [] };
  }
  const source = plainObject(analysis);
  if (!source) {
    return { key: "analysis", arm: "unreadable", summary: null, confidence: null, riskLevel: null, degraded: null, evidenceSources: [] };
  }
  const evidenceSources = Array.isArray(source.evidence_sources)
    ? source.evidence_sources.filter((entry) => text(entry))
    : [];
  return {
    key: "analysis",
    arm: Object.keys(source).length === 0 ? "empty" : "present",
    summary: text(source.summary),
    confidence: finiteNonNegative(source.confidence),
    riskLevel: text(source.risk_level),
    degraded: text(source.degraded),
    evidenceSources,
  };
}

/** The second stage: the action the employee proposed, if it proposed one. */
export function readProposalStage(analysis) {
  const source = plainObject(analysis);
  const proposal = source ? plainObject(source.proposed_action) : null;
  if (!proposal) {
    return { key: "proposal", arm: "absent", description: null, actionType: null, target: null };
  }
  const description = text(proposal.description);
  const actionType = text(proposal.action_type);
  return {
    key: "proposal",
    arm: description || actionType ? "present" : "empty",
    description,
    actionType,
    target: text(proposal.target),
  };
}

/**
 * One approval-token row, bounded. `state` is derived from the row's own flags and expiry; an
 * unreadable or absent expiry leaves an unused, unrevoked token UNKNOWN rather than active.
 */
export function normalizeApprovalToken(raw, now = Date.now()) {
  const source = plainObject(raw);
  if (!source) return null;
  const used = source.used === true;
  const revoked = source.revoked === true;
  const expiresAt = text(source.expires_at);
  let state;
  if (revoked) {
    state = "revoked";
  } else if (used) {
    state = "used";
  } else if (expiresAt) {
    const at = parseUtc(expiresAt).getTime();
    state = Number.isNaN(at) ? "unknown" : at <= now ? "expired" : "active";
  } else {
    state = "unknown";
  }
  return {
    id: text(source.id),
    actionType: text(source.action_type),
    target: text(source.target),
    approverId: text(source.approver_id),
    approvedAt: text(source.approved_at),
    expiresAt,
    riskLevel: source.risk_level,
    used,
    revoked,
    expired: state === "expired",
    state,
    stateLabel: APPROVAL_TOKEN_STATE_LABEL[state],
  };
}

/**
 * The third stage: whether a human decision is open, and the approval tokens recorded for the run.
 *
 * `human-decision` is the same set the approve/reject routes accept (`_APPROVABLE_STATES`, read
 * through `employeeOperations.isHumanDecisionStatus`). A paused run is `resumable` — a different
 * decision on a different control, which the approve/reject routes refuse.
 */
export function readApprovalStage(run, tokens, { now = Date.now(), loading = false } = {}) {
  const status = text(run?.status);
  const decision = isHumanDecisionStatus(status)
    ? "human-decision"
    : status === "paused"
      ? "resumable"
      : "none";
  if (loading) return { key: "approval", arm: "loading", decision, tokens: [] };
  if (!Array.isArray(tokens)) return { key: "approval", arm: "unreported", decision, tokens: [] };
  const normalized = tokens.map((token) => normalizeApprovalToken(token, now)).filter(Boolean);
  return {
    key: "approval",
    arm: normalized.length > 0 ? "recorded" : "empty",
    decision,
    tokens: normalized,
  };
}

/** One action row, bounded. Rows without an id are kept: they are still recorded evidence. */
export function normalizeAction(raw) {
  const source = plainObject(raw);
  if (!source) return null;
  return {
    id: text(source.id),
    sequenceNo: typeof source.sequence_no === "number" && Number.isFinite(source.sequence_no) ? source.sequence_no : null,
    description: text(source.description),
    actionType: text(source.action_type),
    outcome: text(source.outcome),
    riskLevel: text(source.risk_level),
    executedAt: text(source.executed_at),
    createdAt: text(source.created_at),
    durationMs: finiteNonNegative(source.duration_ms),
    failed: source.outcome === "failed",
  };
}

/** The fourth stage: recorded tool calls in sequence order. */
export function readExecutionStage(run) {
  if (!Array.isArray(run?.actions)) {
    return { key: "execution", arm: "unreported", actions: [], failedCount: 0 };
  }
  const actions = run.actions
    .map(normalizeAction)
    .filter(Boolean)
    .sort((a, b) => {
      // A row without a sequence number sorts last, not first: an unknown position is not step one.
      if (a.sequenceNo == null && b.sequenceNo == null) return 0;
      if (a.sequenceNo == null) return 1;
      if (b.sequenceNo == null) return -1;
      return a.sequenceNo - b.sequenceNo;
    });
  return {
    key: "execution",
    arm: actions.length > 0 ? "recorded" : "empty",
    actions,
    failedCount: actions.filter((action) => action.failed).length,
  };
}

/**
 * The fifth stage: the server's own verification judgement. This module does not re-derive any arm
 * — `verdict`, `closure` and `missing_evidence` are copied, and an absent projection stays absent.
 */
export function readVerificationStage(projection) {
  if (projection == null) return { key: "verification", arm: "absent" };
  const source = plainObject(projection);
  if (!source) return { key: "verification", arm: "unreadable" };
  const closure = plainObject(source.closure);
  return {
    key: "verification",
    arm: "recorded",
    verdict: text(source.verdict),
    verdictReason: text(source.verdict_reason),
    verdictDetail: plainObject(source.verdict_detail),
    findingsCount: Array.isArray(source.findings) ? source.findings.length : null,
    closure: closure
      ? { eligible: closure.eligible === true, reason: text(closure.reason) }
      : null,
    missing: Array.isArray(source.missing_evidence)
      ? source.missing_evidence
          .map((entry) => plainObject(entry))
          .filter(Boolean)
          .map((entry) => ({ kind: text(entry.kind), status: text(entry.status) }))
      : [],
    authority: plainObject(source.authority),
    generatedAt: text(source.generated_at),
  };
}

/**
 * Latency and cost facts, read field by field. `null` means the producer did not report the
 * figure; `0` is a reported zero and is returned as `0`.
 */
export function readTelemetry(run) {
  const source = plainObject(run) || {};
  const context = plainObject(source.context);
  return {
    provider: text(source.provider),
    model: text(source.model),
    inputTokens: finiteNonNegative(source.input_tokens),
    outputTokens: finiteNonNegative(source.output_tokens),
    cachedTokens: finiteNonNegative(source.cached_tokens),
    toolCalls: finiteNonNegative(source.tool_calls),
    durationMs: finiteNonNegative(source.duration_ms),
    estimatedCost: finiteNonNegative(source.estimated_cost),
    currency: text(source.currency),
    initiatedBy: text(source.initiated_by),
    // The check name is part of the run's identity only for a system check; on every other run the
    // same key is unrelated context and is not a fact about this run's work.
    checkName: context && context.system_check === true ? text(context.check_name) : null,
  };
}

/**
 * The whole trail over the run page's three reads. The returned `stages` are in
 * `RUN_TRAIL_STAGE_KEYS` order, which is the order the operator follows.
 */
export function buildRunTrail({ run, approvalTokens, tokensLoading = false, qaInspection, now = Date.now() } = {}) {
  const analysis = plainObject(run)?.analysis;
  return {
    stages: [
      readAnalysisStage(analysis),
      readProposalStage(analysis),
      readApprovalStage(run, approvalTokens, { now, loading: tokensLoading }),
      readExecutionStage(run),
      readVerificationStage(qaInspection),
    ],
    telemetry: readTelemetry(run),
    runStatus: text(run?.status),
  };
}
